import assessmentApi from '../pages/assessment/assessmentApi';
import { resolveAssessmentApiBaseUrl } from '../pages/assessment/assessmentApiConfig';

export const testService = {
  getTests: async () => {
    const response = await assessmentApi.get('/assessment/getTests');
    return response.data;
  },
  getCategories: async () => {
    const response = await assessmentApi.get('/assessment/getCategories');
    return response.data;
  },
  createTest: async (data) => {
    const response = await assessmentApi.post('/assessment/createTest', data);
    return response.data;
  },
  updateTest: async (id, data) => {
    const response = await assessmentApi.post(`/assessment/updateTest/${id}`, data);
    return response.data;
  },
  deleteTest: async (id) => {
    const response = await assessmentApi.post(`/assessment/deleteTest/${id}`);
    return response.data;
  },
  saveTemplate: async (data) => {
    const response = await assessmentApi.post('/assessment/saveTemplate', data);
    return response.data;
  },
  deleteTemplate: async (id) => {
    const response = await assessmentApi.post(`/assessment/deleteTemplate/${id}`);
    return response.data;
  },
  uploadIntroVideos: async (testId, files) => {
    const fd = new FormData();
    files.forEach((file) => fd.append('videos[]', file));
    const response = await assessmentApi.post(`/assessment/uploadIntroVideos/${testId}`, fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Question Bank
  getQuestionBanks: async (options = {}) => {
    const includeQuestions = options.includeQuestions === true;
    const query = includeQuestions ? '?include_questions=1' : '';
    const response = await assessmentApi.get(`/assessment/getQuestionBanks${query}`);
    return response.data;
  },
  getQuestionBank: async (id) => {
    const response = await assessmentApi.get(`/assessment/getQuestionBank/${id}`);
    return response.data;
  },
  getPedagogies: async () => {
    const response = await assessmentApi.get('/assessment/getPedagogies');
    return response.data;
  },
  savePedagogy: async (name) => {
    const response = await assessmentApi.post('/assessment/savePedagogy', { name });
    return response.data;
  },
  deletePedagogies: async (names) => {
    const response = await assessmentApi.post('/assessment/deletePedagogies', { names });
    return response.data;
  },
  saveQuestionBank: async (data) => {
    const response = await assessmentApi.post('/assessment/saveQuestionBank', data);
    return response.data;
  },
  renameQuestionBank: async (id, name) => {
    const response = await assessmentApi.post(`/assessment/renameQuestionBank/${id}`, { name });
    return response.data;
  },
  deleteQuestionBank: async (id) => {
    const response = await assessmentApi.post(`/assessment/deleteQuestionBank/${id}`);
    return response.data;
  },
  bulkSaveQBQuestions: async (data) => {
    const response = await assessmentApi.post('/assessment/bulkSaveQBQuestions', data);
    return response.data;
  },
  updateQBQuestionStatus: async (questionId, status) => {
    const response = await assessmentApi.post(`/assessment/updateQBQuestionStatus/${questionId}`, { status });
    return response.data;
  },

  // Templates
  getTemplates: async (options = {}) => {
    const includeQuestions = options.includeQuestions === true;
    const query = includeQuestions ? '?include_questions=1' : '';
    const response = await assessmentApi.get(`/assessment/getTemplates${query}`);
    return response.data;
  },
  getTemplate: async (id) => {
    const response = await assessmentApi.get(`/assessment/getTemplate/${id}`);
    return response.data;
  },
  saveTemplate: async (data) => {
    const response = await assessmentApi.post('/assessment/saveTemplate', data);
    return response.data;
  },
  deleteTemplate: async (id) => {
    const response = await assessmentApi.post(`/assessment/deleteTemplate/${id}`);
    return response.data;
  },

  saveTestPack: async (data) => {
    const response = await assessmentApi.post('/assessment/saveTestPack', data);
    return response.data;
  },
  updateTestPack: async (id, data) => {
    const response = await assessmentApi.post(`/assessment/updateTestPack/${id}`, data);
    return response.data;
  },
  deleteTestPack: async (id) => {
    const response = await assessmentApi.post(`/assessment/deleteTestPack/${id}`);
    return response.data;
  },
  publishTestPack: async (id) => {
    const response = await assessmentApi.post(`/assessment/publishTestPack/${id}`);
    return response.data;
  },
  getBaseURL: () => resolveAssessmentApiBaseUrl(),
  getImageBaseURL: () => resolveAssessmentApiBaseUrl().replace(/\/api$/i, ''),
  getJobRoles: async () => {
    const response = await assessmentApi.get('/assessment/getJobRoles');
    return response.data;
  },
  getUsersByRoles: async (roles) => {
    const response = await assessmentApi.post('/assessment/getUsersByRoles', { roles });
    return response.data;
  },
  getStudentAssessments: async () => {
    const response = await assessmentApi.get('/assessment/getStudentAssessments');
    return response.data;
  },
  getTestExecutionData: async (packId) => {
    const response = await assessmentApi.get(`/assessment/getTestExecutionData/${packId}`);
    return response.data;
  },
  getPackQuestions: async (packId, templateId) => {
    const url = `/assessment/getPackQuestions/${packId}${templateId ? `?template_id=${templateId}` : ''}`;
    const response = await assessmentApi.get(url);
    return response.data;
  },
  saveSubmission: async (data, files = []) => {
    const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
    if (fileList.length > 0) {
      const fd = new FormData();
      fd.append('assessment_id', String(data.assessment_id ?? ''));
      fd.append('test_pack_id', String(data.test_pack_id ?? ''));
      fd.append('user_id', String(data.user_id ?? ''));
      fd.append('answers', JSON.stringify(data.answers ?? {}));
      fd.append('mcq_score', String(data.mcq_score ?? 0));
      fd.append('overall_pct', String(data.overall_pct ?? 0));
      fd.append('status', String(data.status ?? 'Completed'));
      if (data.started_at) {
        fd.append('started_at', String(data.started_at));
      }
      fileList.forEach((file) => {
        fd.append('files[]', file);
      });
      // Do not set Content-Type — browser must add multipart boundary automatically.
      const response = await assessmentApi.post('/assessment/saveSubmission', fd, {
        timeout: 120000,
      });
      return response.data;
    }
    const response = await assessmentApi.post('/assessment/saveSubmission', data);
    return response.data;
  },
  getSubmissions: async (assessmentId = '') => {
    const response = await assessmentApi.get(`/assessment/getSubmissions?assessment_id=${assessmentId}`);
    return response.data;
  },
  updateSubmissionScore: async (data) => {
    const response = await assessmentApi.post('/assessment/updateSubmissionScore', data);
    return response.data;
  },

  // HR Upload
  getColleges: async () => {
    const response = await assessmentApi.get('/assessment/getColleges');
    return response.data;
  },
  bulkSaveStudents: async (data) => {
    const response = await assessmentApi.post('/assessment/bulkSaveStudents', data);
    return response.data;
  },
  getStudentsByCollege: async (collegeName) => {
    const response = await assessmentApi.get(`/assessment/getStudentsByCollege?college=${encodeURIComponent(collegeName)}`);
    return response.data;
  },
  deleteStudent: async (id) => {
    const response = await assessmentApi.post(`/assessment/deleteStudent/${id}`);
    return response.data;
  },
  getHrCollegeTemplates: async () => {
    const response = await assessmentApi.get('/assessment/hr-module/college-templates');
    return response.data;
  }

};

export default testService;